(() => {
  'use strict';
  // Figured borders (kids-border + figured-border products, marseladesign.com 2026-10-25, ported from the Türkiye
  // store). The customer enters the wall width and the band height (80–150 cm, cm or in; the 300 × 102 cm scene is
  // scaled to it), picks the point of the design that starts at the left edge of the wall, and a material; the quote
  // worker prices and freezes the selection (USD, or EUR for the Europe market) and this script adds exactly that
  // variant to the cart. Shared logic: assets/kind-core.js. Must match FIGURED_BORDERS / FIGURED_BORDER_GEOMETRY in the
  // worker (com-tasima/desen-bordur/arac/add_kinds_com.py) and snippets/figured-border-registry.liquid.
  const K = globalThis.MarselaKinds;
  if (!K) return;
  const {KindError, finite, fill, MATERIALS, MAX_WIDTH_CM} = K;
  const SCENE = Object.freeze({label: 'Figured border', tileWidthCm: 300, tileHeightCm: 102, origin: 'top-left'});
  const MIN_HEIGHT_CM = 80, MAX_HEIGHT_CM = 150;
  // Preview only: the painted wall above the band, and a crib (135 × 90 cm) for scale.
  const ROOM_HEIGHT_CM = 260, STRIP_CSS_HEIGHT = 150;
  const REFERENCES = Object.freeze({crib: {widthCm: 135, heightCm: 90, right: 0.06}});
  const EXAMPLE = Object.freeze({widthCm: 300, heightCm: 102});
  const FORM_TEXT = Object.freeze({
    example: 'Example: 300 cm wide, border 102 cm. Enter your own size.',
    caption: 'Your wall: {width} wide · border {height} · the design repeats every {repeat} · ceiling shown at {room}',
    start: 'Start: {start} cm into the design',
    selection: 'Please enter the wall width and the border height first.'
  });
  const validStart = value => Number.isInteger(value) && value >= 0 && value < SCENE.tileWidthCm;
  const repeatCm = heightCm => SCENE.tileWidthCm * heightCm / SCENE.tileHeightCm;

  function sceneFrom(data) {
    const scene = {handle: data?.figuredHandle, reference: data?.figuredReference, sha256: data?.figuredSha256, asset: data?.figuredAsset, sourceWidth: Number(data?.figuredWidth), sourceHeight: Number(data?.figuredHeight), src: data?.figuredSrc, band: /^#[0-9a-fA-F]{6}$/.test(data?.figuredBand || '') ? data.figuredBand : '#6f7f75'};
    if (typeof scene.handle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(scene.handle) || !/^MWC18\d{2}$/.test(scene.reference || '') || !scene.handle.endsWith(`-${scene.reference.toLowerCase()}`)) return null;
    if (!/^[a-f0-9]{64}$/.test(scene.sha256 || '') || scene.asset !== `customizer-border-${scene.reference.toLowerCase()}.jpg` || scene.sourceWidth !== 3600 || scene.sourceHeight !== 1224 || typeof scene.src !== 'string' || !scene.src) return null;
    return Object.freeze(scene);
  }
  function canonicalConfiguration(input, scene) {
    if (!scene || input?.schemaVersion !== 1 || input.kind !== 'figured-border' || input.productHandle !== scene.handle || input.artwork?.reference !== scene.reference || input.artwork?.sha256 !== scene.sha256) throw new KindError('selection');
    const wall = input.wall;
    if (!wall || !finite(wall.widthCm) || !finite(wall.heightCm) || wall.widthCm <= 0 || wall.widthCm > MAX_WIDTH_CM || wall.heightCm < MIN_HEIGHT_CM || wall.heightCm > MAX_HEIGHT_CM) throw new KindError('selection');
    if (!validStart(input.startCm)) throw new KindError('selection');
    if (typeof input.material !== 'string' || !Object.hasOwn(MATERIALS, input.material)) throw new KindError('selection');
    return {schemaVersion: 1, kind: 'figured-border', productHandle: scene.handle, artwork: {reference: scene.reference, sha256: scene.sha256}, wall: {widthCm: wall.widthCm, heightCm: wall.heightCm}, startCm: input.startCm === 0 ? 0 : input.startCm, material: input.material};
  }
  function validateQuote(data, configuration, scene, endpoint, ready, currency = 'USD') {
    const c = data?.configuration;
    let returned;
    try { returned = canonicalConfiguration({schemaVersion: c?.schemaVersion, kind: c?.kind, productHandle: c?.productHandle, artwork: {reference: c?.artwork?.reference, sha256: c?.artwork?.sha256}, wall: c?.wall, startCm: c?.figuredBorder?.startCm, material: c?.material?.id}, scene); } catch (_) { throw new KindError('quote'); }
    if (JSON.stringify(returned) !== JSON.stringify(configuration)) throw new KindError('quote');
    const f = c.figuredBorder, h = configuration.wall.heightCm;
    if (f?.label !== SCENE.label || f.tileWidthCm !== SCENE.tileWidthCm || f.tileHeightCm !== SCENE.tileHeightCm || f.origin !== SCENE.origin || !finite(f.scale) || Math.abs(f.scale - h / SCENE.tileHeightCm) > 1e-9 || !finite(f.repeatCm) || Math.abs(f.repeatCm - repeatCm(h)) > 1e-6) throw new KindError('quote');
    return K.validateCommon(data, configuration, scene, endpoint, ready, currency, 'printSha256');
  }
  function properties(quoteData) {
    const c = quoteData.configuration;
    return K.baseProperties(quoteData, {'Wall width': `${K.plain(c.wall.widthCm)} cm`, 'Border height': `${K.plain(c.wall.heightCm)} cm`, 'Start point': `${c.figuredBorder.startCm} cm into the design`});
  }
  // Exercised by com-tasima/desen-bordur/arac/test_kinds_contract.mjs.
  if (typeof module === 'object' && module.exports) module.exports = {SCENE, sceneFrom, canonicalConfiguration, repeatCm, validateQuote, properties};
  if (typeof document === 'undefined') return;

  const sized = (src, width) => { try { const url = new URL(src, location.href); url.searchParams.set('width', String(width)); return url.href; } catch (_) { return src; } };
  const lighten = (hex, amount) => { const n = parseInt(hex.slice(1), 16), mix = v => Math.round(v + (255 - v) * amount); return `rgb(${mix(n >> 16)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`; };
  // Draws the band (scene scaled to the band height, starting at startCm) into a box of the canvas.
  function drawScene(ctx, image, wall, startCm, left, top, width, height) {
    if (!image.complete || !image.naturalWidth) return;
    const tw = width * repeatCm(wall.heightCm) / wall.widthCm, x0 = -startCm / SCENE.tileWidthCm * tw;
    ctx.save(); ctx.beginPath(); ctx.rect(left, top, width, height); ctx.clip();
    for (let x = left + x0; x < left + width; x += tw) ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, x, top, tw + 0.5, height);
    ctx.restore();
  }
  function drawJoins(ctx, wall, top, width, height, ratio) {
    const plan = K.panelPlan(wall);
    ctx.save(); ctx.strokeStyle = 'rgba(38,62,53,.5)'; ctx.lineWidth = ratio; ctx.setLineDash([5 * ratio, 4 * ratio]);
    for (let index = 1; index < plan.count; index++) { const x = Math.round(width * index / plan.count) + 0.5; ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, top + height); ctx.stroke(); }
    ctx.restore();
  }

  function mount(form) {
    if (form.dataset.msFiguredReady) return;
    const scene = sceneFrom(form.dataset);
    if (!scene) return;
    form.dataset.msFiguredReady = 'true';
    const $ = selector => form.querySelector(selector), $$ = selector => [...form.querySelectorAll(selector)];
    const width = $('[data-ms-figured-width]'), height = $('[data-ms-figured-height]'), error = $('[data-ms-figured-error]'), start = $('[data-ms-figured-start]'), startLabel = $('[data-ms-figured-start-label]');
    const materials = $$('[data-ms-figured-material]');
    const stage = $('[data-ms-figured-stage]'), wallBox = $('[data-ms-figured-wall]'), canvas = $('[data-ms-figured-canvas]'), caption = $('[data-ms-figured-caption]');
    const strip = $('[data-ms-figured-strip]'), stripCanvas = $('[data-ms-figured-strip-canvas]');
    const produced = $('[data-ms-figured-produced]'), panels = $('[data-ms-figured-panels]'), area = $('[data-ms-figured-area]'), repeat = $('[data-ms-figured-repeat]'), total = $('[data-ms-figured-total]'), priceLabel = $('[data-ms-figured-price-label]');
    const button = $('[data-ms-figured-add]'), buttonLabel = $('[data-ms-figured-add-label]'), status = $('[data-ms-figured-status]'), viewCart = $('[data-ms-figured-cart]');
    const crib = $('[data-ms-figured-ref="crib"]');
    if (!width || !height || !start || !canvas || !wallBox || !button || !status) return;
    const page = K.pageContext(), fmt = K.formatter(page.locale), TEXT = K.textFor(form, FORM_TEXT, page.eurEnabled);
    const client = K.createClient({endpoint: page.endpoint, root: page.root});
    const enabled = page.enabled && Boolean(client.service);
    const store = K.storage(`marsela-figured-border-v1:${scene.handle}:${K.RATE_VERSION}`);
    const paint = lighten(scene.band, 0.88);
    viewCart.href = client.route('cart');
    const units = K.bindUnit(form, [width, height], () => changed());

    const image = new Image();
    image.decoding = 'async';
    image.addEventListener('load', () => schedule());
    image.src = sized(scene.src, 1800);

    let phase = 'idle', errorCode = null, busy = false, slowTimer = 0, slow = false;
    const chosen = list => list.find(input => input.checked)?.value;
    const startValue = () => { const value = Number(start.value); return validStart(value) ? value : 0; };
    function readWall() {
      const w = units.read(width), h = units.read(height);
      const widthBad = width.value.trim() !== '' && (w === null || w > MAX_WIDTH_CM);
      const heightBad = height.value.trim() !== '' && (h === null || h < MIN_HEIGHT_CM || h > MAX_HEIGHT_CM);
      const valid = w !== null && h !== null && w <= MAX_WIDTH_CM && h >= MIN_HEIGHT_CM && h <= MAX_HEIGHT_CM;
      return {valid, typedWidth: w !== null && w <= MAX_WIDTH_CM ? w : null, wall: valid ? {widthCm: w, heightCm: h} : null, invalid: widthBad || heightBad};
    }
    function currentConfiguration() {
      const state = readWall();
      if (!state.valid) return null;
      try { return canonicalConfiguration({schemaVersion: 1, kind: 'figured-border', productHandle: scene.handle, artwork: {reference: scene.reference, sha256: scene.sha256}, wall: state.wall, startCm: startValue(), material: chosen(materials)}, scene); } catch (_) { return null; }
    }
    function draw() {
      const state = readWall(), wall = state.wall || {widthCm: state.typedWidth || EXAMPLE.widthCm, heightCm: EXAMPLE.heightCm};
      const startCm = startValue(), ratio = Math.min(2, window.devicePixelRatio || 1);
      // Whole wall: painted wall, the band from the floor up, panel joins, a crib for scale.
      const maxWidth = stage.clientWidth || 320, maxHeight = Math.min(360, Math.max(180, window.innerHeight * 0.42));
      let boxWidth = maxWidth, boxHeight = maxWidth * ROOM_HEIGHT_CM / wall.widthCm;
      if (boxHeight > maxHeight) { boxHeight = maxHeight; boxWidth = maxHeight * wall.widthCm / ROOM_HEIGHT_CM; }
      wallBox.style.width = `${Math.max(24, boxWidth)}px`; wallBox.style.height = `${Math.max(24, boxHeight)}px`;
      canvas.width = Math.max(1, Math.round(boxWidth * ratio)); canvas.height = Math.max(1, Math.round(boxHeight * ratio));
      const ctx = canvas.getContext('2d'), perCm = canvas.height / ROOM_HEIGHT_CM, bandHeight = wall.heightCm * perCm, top = canvas.height - bandHeight;
      ctx.fillStyle = paint; ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawScene(ctx, image, wall, startCm, 0, top, canvas.width, bandHeight);
      drawJoins(ctx, wall, top, canvas.width, bandHeight, ratio);
      if (crib) {
        crib.style.width = `${Math.min(100, REFERENCES.crib.widthCm / wall.widthCm * 100)}%`;
        crib.style.height = `${Math.min(100, REFERENCES.crib.heightCm / ROOM_HEIGHT_CM * 100)}%`;
        crib.style.right = `${REFERENCES.crib.right * 100}%`;
      }
      // Close-up: the band alone at a fixed height, scrollable sideways.
      if (strip && stripCanvas) {
        const cssHeight = stripCanvas.clientHeight || STRIP_CSS_HEIGHT, cssWidth = Math.max(1, Math.round(cssHeight * wall.widthCm / wall.heightCm));
        stripCanvas.style.width = `${cssWidth}px`;
        stripCanvas.width = Math.max(1, Math.round(cssWidth * ratio)); stripCanvas.height = Math.max(1, Math.round(cssHeight * ratio));
        const sctx = stripCanvas.getContext('2d');
        sctx.fillStyle = paint; sctx.fillRect(0, 0, stripCanvas.width, stripCanvas.height);
        drawScene(sctx, image, wall, startCm, 0, 0, stripCanvas.width, stripCanvas.height);
        drawJoins(sctx, wall, 0, stripCanvas.width, stripCanvas.height, ratio);
      }
      const unit = units.unit;
      if (startLabel) startLabel.textContent = fill(TEXT.start, {start: fmt.number(startCm, 0)});
      caption.textContent = state.wall ? fill(TEXT.caption, {width: fmt.length(wall.widthCm, unit), height: fmt.length(wall.heightCm, unit), repeat: fmt.length(repeatCm(wall.heightCm), unit), room: fmt.length(ROOM_HEIGHT_CM, unit)}) : TEXT.example;
    }
    let frame = 0;
    function schedule() { if (!frame) frame = requestAnimationFrame(() => { frame = 0; draw(); }); }
    function render() {
      const state = readWall(), configuration = currentConfiguration();
      error.hidden = !state.invalid;
      if (configuration) {
        const plan = K.panelPlan(configuration.wall), m2 = configuration.wall.widthCm * configuration.wall.heightCm / 10000, areaText = `${fmt.number(m2, 2)} m²`;
        produced.textContent = fmt.size(configuration.wall, units.unit);
        panels.textContent = `${plan.count} × ${fmt.length(plan.panelWidthCm, units.unit)}`;
        area.textContent = m2 < K.MIN_BILLABLE_M2 ? fill(TEXT.pricedAs, {area: areaText}) : areaText;
        repeat.textContent = fmt.length(repeatCm(configuration.wall.heightCm), units.unit);
        total.textContent = fmt.money(K.expectedPrice(configuration, page.displayCurrency), page.displayCurrency);
      } else { produced.textContent = '—'; panels.textContent = '—'; area.textContent = '—'; repeat.textContent = '—'; total.textContent = '—'; }
      if (priceLabel) priceLabel.textContent = phase === 'added' ? TEXT.price : TEXT.estimate;
      buttonLabel.textContent = phase === 'preparing' ? TEXT.preparing : phase === 'adding' || phase === 'added' ? TEXT.adding : phase === 'uncertain' ? TEXT.view : phase === 'failed' ? TEXT.retry : TEXT.confirmAdd;
      button.disabled = busy || !enabled;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      status.textContent = !enabled ? TEXT.unavailable : errorCode ? (TEXT[errorCode] || TEXT.network) : phase === 'preparing' ? (slow ? TEXT.slow : TEXT.preparingNote) : phase === 'adding' ? TEXT.adding : phase === 'added' ? TEXT.added : TEXT.idle;
      viewCart.hidden = !(phase === 'uncertain' || phase === 'added');
      schedule();
    }
    function changed() {
      if (busy) return;
      if (phase !== 'idle' && phase !== 'uncertain') { phase = 'idle'; errorCode = null; }
      render();
    }
    [width, height].forEach(field => {
      field.addEventListener('input', changed);
      field.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); field.blur(); } });
    });
    start.addEventListener('input', changed);
    materials.forEach(input => input.addEventListener('change', changed));
    form.addEventListener('submit', event => event.preventDefault());
    [canvas, stripCanvas].forEach(element => element?.addEventListener('contextmenu', event => event.preventDefault()));

    button.addEventListener('click', async () => {
      if (phase === 'uncertain') { location.assign(client.route('cart')); return; }
      if (busy || !enabled) return;
      const configuration = currentConfiguration();
      if (!configuration) {
        errorCode = 'selection'; phase = 'failed'; render();
        (units.read(width) === null ? width : height).focus();
        return;
      }
      busy = true; phase = 'preparing'; errorCode = null; slow = false; render();
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 75000);
      slowTimer = setTimeout(() => { slow = true; render(); }, 25000);
      try {
        const current = await client.cart(controller.signal), currency = current.currency;
        if (!K.currencyAllowed(currency, page.eurEnabled)) throw new KindError('currency');
        const key = K.reuseKey(store.read(value => canonicalConfiguration(value, scene)), configuration, currency);
        store.write({configuration, idempotencyKey: key, currency, unit: units.unit});
        const ready = await client.quote(configuration, currency, key, controller.signal, (data, isReady) => validateQuote(data, configuration, scene, client.service, isReady, currency));
        clearTimeout(slowTimer); phase = 'adding'; render();
        await client.add(ready, currency, properties(ready), controller.signal);
        phase = 'added'; render();
        location.assign(client.route('cart'));
      } catch (failure) {
        const code = failure?.name === 'AbortError' || failure?.code === 'cancelled' ? (phase === 'adding' ? 'uncertain' : 'pending') : typeof failure?.code === 'string' && Object.hasOwn(TEXT, failure.code) ? failure.code : 'network';
        errorCode = code; phase = code === 'uncertain' ? 'uncertain' : 'failed';
      } finally { clearTimeout(timeout); clearTimeout(slowTimer); busy = false; slow = false; render(); }
    });

    const stored = store.read(value => canonicalConfiguration(value, scene));
    if (stored) {
      units.set(stored.unit);
      width.value = units.show(stored.configuration.wall.widthCm);
      height.value = units.show(stored.configuration.wall.heightCm);
      start.value = String(stored.configuration.startCm);
      materials.forEach(input => { input.checked = input.value === stored.configuration.material; });
    }
    if (typeof ResizeObserver === 'function') new ResizeObserver(schedule).observe(stage);
    else window.addEventListener('resize', schedule);
    render();
  }

  function init(scope = document) { scope.querySelectorAll('[data-ms-figured-entry]').forEach(mount); }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
